import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  Typography,
  CircularProgress,
  Avatar,
  Button,
  Chip,
  Breadcrumbs,
  ThemeProvider,
  Rating,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CategoryIcon from "@mui/icons-material/Category";
import { getCategoryProducts, getCategories } from "../../../../../services/Public/PublicPagesApi";
import { getImageUrl } from "../../../../../services/indexMediaBaseUrl";
import { luxuryTheme } from "../../../../../theme/luxuryTheme";

const CategoriesPage = ({ categoryId: propCategoryId }) => {
  const { id: paramId } = useParams();
  const categoryId = propCategoryId || paramId;
  const { t, i18n } = useTranslation();
  let lang = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
  if (lang === "rn") lang = "en";
  lang = lang.startsWith("ar") ? "ar" : "en";

  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(Boolean(categoryId));
  const [error, setError] = useState(null);

  // NEW: categories list state + tree
  const [allCategories, setAllCategories] = useState([]);
  const [catsLoading, setCatsLoading] = useState(false);
  const [catsError, setCatsError] = useState(null);
  const [catTree, setCatTree] = useState([]);

  useEffect(() => {
    if (!categoryId) return;
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await getCategoryProducts(categoryId);
        if (res && res.success) {
          setCategory(res.category || null);
          setProducts(Array.isArray(res.products) ? res.products : []);
        } else {
          setCategory(null);
          setProducts([]);
        }
      } catch (err) {
        setError(err.message || "Error");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [categoryId]);

  // fetch all categories and build parent-children tree
  useEffect(() => {
    // تحديث بناء الشجرة ليصبح 3 مستويات (parent -> children -> grandchildren)
    const fetchCats = async () => {
      try {
        setCatsLoading(true);
        const res = await getCategories();
        if (res && res.success) {
          const list = Array.isArray(res.data) ? res.data : [];
          setAllCategories(list);

          // بناء خريطة الأطفال حسب parent id
          const childrenMap = list.reduce((acc, cat) => {
            const pid = cat.category_id;
            if (pid && cat.status !== 0) {
              if (!acc[pid]) acc[pid] = [];
              acc[pid].push(cat);
            }
            return acc;
          }, {});

          // الآباء (category_id === null) - استبعاد الفئات بـ status = 0
          const parents = list.filter((c) => !c.category_id && c.status !== 0);

          // لكل أب، نضيف children (المستوى الثاني) وداخل كل child نضيف grandchildren (المستوى الثالث)
          const structured = parents.map((p) => {
            const children = (childrenMap[p.id] || []).map((child) => ({
              ...child,
              children: (childrenMap[child.id] || []).filter((gc) => gc.status !== 0),
            }));
            return {
              ...p,
              children,
            };
          });

          setCatTree(structured);
        } else {
          setAllCategories([]);
          setCatTree([]);
        }
      } catch (err) {
        setCatsError(err.message || "Error");
      } finally {
        setCatsLoading(false);
      }
    };
    fetchCats();
  }, []);

  const getName = (p) => (lang === "ar" ? p.name_ar || p.name_en : p.name_en || p.name_ar);
  const getDesc = (p) => (lang === "ar" ? p.desc_ar || "" : p.desc_en || "");
  const getProductImage = (p) => getImageUrl(p.main_photo || (p.images && p.images[0] && p.images[0].image) || "");

  if (!categoryId) return null;

  if (loading) {
    return (
      <ThemeProvider theme={luxuryTheme}>
        <Box 
          sx={{ 
            py: 8, 
            display: "flex", 
            justifyContent: "center",
            minHeight: "60vh",
            alignItems: "center",
            background: luxuryTheme.palette.background.gradient
          }}
        >
          <CircularProgress size={60} thickness={4} sx={{ color: "primary.main" }} />
        </Box>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={luxuryTheme}>
        <Box sx={{ py: 8, textAlign: "center", minHeight: "40vh" }}>
          <Typography color="error" variant="h6" sx={{ fontWeight: 500 }}>
            {t("error_loading_category_products", { defaultValue: lang === "ar" ? "حدث خطأ في تحميل منتجات الصنف" : "Error loading category products" })}
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={luxuryTheme}>
      <Box 
        sx={{ 
          py: { xs: 4, md: 6 },
          px: 2,
          background: luxuryTheme.palette.background.gradient,
          minHeight: "100vh"
        }}
      >
        <Container maxWidth="xl">
          {/* Breadcrumbs */}
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" sx={{ transform: lang === "ar" ? "rotate(180deg)" : "none" }} />}
            sx={{ mb: 3 }}
          >
            <Link 
              to="/" 
              style={{ 
                textDecoration: "none", 
                color: luxuryTheme.palette.text.secondary,
                display: "flex",
                alignItems: "center",
                gap: 4
              }}
            >
              <HomeIcon fontSize="small" />
              <Typography variant="body2">{lang === "ar" ? "الرئيسية" : "Home"}</Typography>
            </Link>
            <Typography 
              variant="body2" 
              sx={{ 
                color: "primary.main",
                fontWeight: 600 
              }}
            >
              {category ? getName(category) : t("category_products", { defaultValue: lang === "ar" ? "منتجات الصنف" : "Category Products" })}
            </Typography>
          </Breadcrumbs>

          {/* Categories Navigation Bar */}
          <Box 
            sx={{ 
              mb: 4,
              background: "#fff",
              borderRadius: 3,
              p: 2,
              border: "1px solid rgba(0,0,0,0.08)",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
            }}
          >
            {catsLoading ? (
              <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
                <CircularProgress size={32} sx={{ color: "primary.main" }} />
              </Box>
            ) : catsError ? (
              <Typography color="error" align="center" sx={{ fontWeight: 500 }}>
                {t("error_loading_categories", { defaultValue: lang === "ar" ? "حدث خطأ في تحميل الفئات" : "Error loading categories" })}
              </Typography>
            ) : (
              <Box sx={{ display: "flex", gap: 2, overflowX: "auto", py: 1, "&::-webkit-scrollbar": { height: 8 }, "&::-webkit-scrollbar-thumb": { background: "primary.light", borderRadius: 4 } }}>
                {catTree.map((parent) => {
                  const isSelectedParent = String(parent.id) === String(categoryId);
                  return (
                    <Card 
                      key={parent.id} 
                      sx={{ 
                        minWidth: 280,
                        flexShrink: 0,
                        p: 2,
                        background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                        border: "1px solid rgba(0,0,0,0.08)",
                        borderRadius: 2,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                          borderColor: "primary.main"
                        }
                      }}
                    >
                      {/* Parent Header */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, pb: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                        <Avatar 
                          src={getImageUrl(parent.image)} 
                          alt={getName(parent)} 
                          variant="rounded" 
                          sx={{ 
                            width: 48,
                            height: 48,
                            border: "2px solid",
                            borderColor: "primary.light",
                            boxShadow: "0 2px 8px rgba(212, 175, 55, 0.3)"
                          }} 
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography 
                            variant="subtitle1" 
                            fontWeight="700"
                            sx={{ 
                              color: "secondary.main",
                              fontSize: "0.95rem",
                              display: "-webkit-box",
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden"
                            }}
                          >
                            {getName(parent)}
                          </Typography>
                          {getDesc(parent) && (
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden"
                              }}
                            >
                              {getDesc(parent)}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Children & Grandchildren */}
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        {parent.children && parent.children.length > 0 ? (
                          parent.children.map((child) => {
                            const isChildSelected = String(child.id) === String(categoryId);
                            return (
                              <Box key={child.id}>
                                <Button
                                  component={Link}
                                  to={`/category/${child.id}`}
                                  variant={isChildSelected ? "contained" : "outlined"}
                                  size="small"
                                  fullWidth
                                  sx={{ 
                                    textTransform: "none",
                                    justifyContent: "flex-start",
                                    fontWeight: isChildSelected ? 700 : 600,
                                    background: isChildSelected ? "linear-gradient(135deg, #d4af37 0%, #f4e5c2 100%)" : "transparent",
                                    "&:hover": {
                                      background: isChildSelected ? "linear-gradient(135deg, #b8941f 0%, #d4af37 100%)" : "rgba(212, 175, 55, 0.1)"
                                    }
                                  }}
                                >
                                  {getName(child)}
                                </Button>

                                {/* Grandchildren */}
                                {child.children && child.children.length > 0 && (
                                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5, ml: 2 }}>
                                    {child.children.map((grand) => {
                                      const isGrandSelected = String(grand.id) === String(categoryId);
                                      return (
                                        <Chip
                                          key={grand.id}
                                          label={getName(grand)}
                                          component={Link}
                                          to={`/category/${grand.id}`}
                                          size="small"
                                          clickable
                                          sx={{
                                            fontSize: "0.7rem",
                                            fontWeight: 600,
                                            background: isGrandSelected 
                                              ? "linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)"
                                              : "linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%)",
                                            color: isGrandSelected ? "#fff" : "text.primary",
                                            border: isGrandSelected ? "none" : "1px solid rgba(0,0,0,0.08)",
                                            transition: "all 0.2s ease",
                                            "&:hover": {
                                              background: isGrandSelected
                                                ? "linear-gradient(135deg, #229954 0%, #27ae60 100%)"
                                                : "linear-gradient(135deg, #d4af37 0%, #f4e5c2 100%)",
                                              color: "#fff",
                                              transform: "scale(1.05)"
                                            }
                                          }}
                                        />
                                      );
                                    })}
                                  </Box>
                                )}
                              </Box>
                            );
                          })
                        ) : (
                          <Button
                            component={Link}
                            to={`/category/${parent.id}`}
                            variant={isSelectedParent ? "contained" : "outlined"}
                            size="small"
                            fullWidth
                            sx={{ 
                              textTransform: "none",
                              fontWeight: 600,
                              background: isSelectedParent ? "linear-gradient(135deg, #d4af37 0%, #f4e5c2 100%)" : "transparent"
                            }}
                          >
                            {t("view_parent", { defaultValue: lang === "ar" ? "عرض" : "View" })}
                          </Button>
                        )}
                      </Box>
                    </Card>
                  );
                })}

                {/* Fallback: Flat List */}
                {catTree.length === 0 && allCategories.length > 0 && (
                  allCategories.filter((cat) => cat.status !== 0).map((cat) => (
                    <Card 
                      key={cat.id} 
                      component={Link} 
                      to={`/category/${cat.id}`}
                      sx={{ 
                        minWidth: 180,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1.5,
                        textDecoration: "none",
                        flexShrink: 0,
                        background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                        border: "1px solid rgba(0,0,0,0.08)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 8px 16px rgba(0,0,0,0.12)",
                          borderColor: "primary.main"
                        }
                      }}
                    >
                      <Avatar 
                        src={getImageUrl(cat.image)} 
                        alt={getName(cat)} 
                        variant="rounded" 
                        sx={{ 
                          width: 48,
                          height: 48,
                          border: "2px solid",
                          borderColor: "primary.light"
                        }} 
                      />
                      <Box>
                        <Typography variant="subtitle2" fontWeight="700" sx={{ color: "secondary.main" }}>
                          {getName(cat)}
                        </Typography>
                        {getDesc(cat) && (
                          <Typography variant="caption" color="text.secondary">
                            {getDesc(cat)}
                          </Typography>
                        )}
                      </Box>
                    </Card>
                  ))
                )}
              </Box>
            )}
          </Box>

          {/* Category Header */}
          <Box 
            sx={{ 
              mb: 5,
              pb: 3,
              borderBottom: "3px solid",
              borderImage: "linear-gradient(90deg, #d4af37 0%, #f4e5c2 50%, #d4af37 100%) 1",
              display: "flex",
              alignItems: "center",
              gap: 2
            }}
          >
            <Box
              sx={{
                background: "linear-gradient(135deg, #d4af37 0%, #f4e5c2 100%)",
                borderRadius: "50%",
                p: 1.5,
                display: "flex",
                boxShadow: "0 4px 20px rgba(212, 175, 55, 0.4)"
              }}
            >
              <CategoryIcon sx={{ fontSize: 32, color: "#fff" }} />
            </Box>
            <Box>
              <Typography 
                variant="h4" 
                fontWeight="800"
                sx={{
                  background: "linear-gradient(135deg, #2c3e50 0%, #34495e 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: "-0.5px"
                }}
              >
                {category ? getName(category) : t("category_products", { defaultValue: lang === "ar" ? "منتجات الصنف" : "Category Products" })}
              </Typography>
              {category && (category.desc_ar || category.desc_en) && (
                <Typography 
                  variant="body1" 
                  color="text.secondary"
                  sx={{ fontWeight: 500, mt: 0.5 }}
                >
                  {getDesc(category)}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Products Grid */}
          {products.length === 0 ? (
            <Box 
              sx={{ 
                textAlign: "center", 
                py: 8,
                px: 2
              }}
            >
              <Typography 
                variant="h5" 
                color="text.secondary"
                fontWeight="600"
              >
                {t("no_products_found", { defaultValue: lang === "ar" ? "لا توجد منتجات" : "No products found" })}
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={4}>
              {products.map((product) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                  <Card 
                    sx={{ 
                      height: "100%",
                      position: "relative",
                      overflow: "visible",
                      borderRadius: 3,
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      border: "1px solid",
                      borderColor: "rgba(0,0,0,0.08)",
                      background: "#fff",
                      "&:hover": { 
                        transform: "translateY(-12px)",
                        boxShadow: 6,
                        borderColor: "primary.main",
                        "& .product-image": {
                          transform: "scale(1.08)"
                        }
                      }
                    }}
                  >
                    <CardActionArea 
                      component={Link} 
                      to={`/product/${product.id}`}
                      sx={{ 
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        p: 2.5
                      }}
                    >
                      {/* Product Image */}
                      <Box 
                        sx={{ 
                          width: "100%",
                          height: 220,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 2,
                          overflow: "hidden",
                          borderRadius: 2,
                          background: "linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%)"
                        }}
                      >
                        <CardMedia
                          component="img"
                          image={getProductImage(product)}
                          alt={getName(product)}
                          className="product-image"
                          sx={{ 
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                          }}
                        />
                      </Box>

                      {/* Product Info */}
                      <Box sx={{ flex: 1, textAlign: "center", width: "100%" }}>
                        <Typography 
                          variant="h6" 
                          fontWeight="700"
                          sx={{
                            mb: 1.5,
                            minHeight: 48,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            color: "secondary.main",
                            lineHeight: 1.3,
                            fontSize: "1rem"
                          }}
                        >
                          {getName(product)}
                        </Typography>

                        {/* Rating */}
                        {product.reviews_summary ? (
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, mb: 1 }}>
                            <Rating 
                              value={parseFloat(product.reviews_summary.average_rating) || 0} 
                              readOnly 
                              size="small"
                              precision={0.5}
                              sx={{
                                "& .MuiRating-iconFilled": {
                                  color: "#ffd700"
                                }
                              }}
                            />
                            <Typography variant="caption" color="text.secondary" fontWeight="600">
                              ({product.reviews_summary.total_reviews || 0})
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" fontWeight="500" sx={{ mb: 1 }}>
                            {t("no_rating", { defaultValue: lang === "ar" ? "بدون تقييم" : "No rating" })}
                          </Typography>
                        )}

                        {/* Description */}
                        {getDesc(product) && (
                          <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              lineHeight: 1.4
                            }}
                          >
                            {getDesc(product)}
                          </Typography>
                        )}
                      </Box>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default CategoriesPage;
